import {
  LinkFormData,
  MainBannerFormData,
  MainBannerFormValidation,
  TabType,
  TextColorType,
  TextFormData,
} from 'types/marketplace/operation/mainBanner/mainBannerForm';
import {
  BANNER_NAME_MAX,
  IMAGE_ACCEPT_TYPES,
  IMAGE_ASPECT_RATIO,
  IMAGE_ASPECT_RATIO_TOLERANCE,
  LINK_URL_MAX,
  RECOMMENDED_IMAGE_HEIGHT,
  RECOMMENDED_IMAGE_WIDTH,
  TEXT_SUBTITLE_MAX,
  TEXT_TITLE_MAX,
} from 'constants/marketplace/mainBanner';
import { BANNER_TEXT_COLORS } from 'constants/marketplace/mainBannerPreview';
import { MainBannerData } from 'types/api/marketplace/operation/mainBanner';
import { convertURLtoFile } from './utils/convert';

export const REQUIRED_MESSAGE = 'This field is required';
export const LINK_INVALID_MESSAGE = 'Please enter a URL starting with http:// or https://';

export const getOverLengthMessage = (max: number): string =>
  `Please enter ${max} characters or fewer`;

/**
 * [EN] Takes either spelling, because the form holds the radio's string and the list holds the
 *      API's enum. Anything that is not black is white — white is the default, so an unknown value
 *      from the server should land on it rather than paint text that cannot be read.
 * [KO] 두 표기를 다 받는다. 폼은 라디오의 문자열을, 목록은 API 의 enum 을 들고 있기 때문이다.
 *      검정이 아니면 흰색이다 — 흰색이 기본값이라, 서버에서 모르는 값이 와도 읽을 수 없는 글자를
 *      그리는 대신 그쪽으로 떨어져야 한다.
 */
export const getBannerTextColor = (textColor: string | TextColorType): string =>
  textColor.toString() === TextColorType.BLACK.toString()
    ? BANNER_TEXT_COLORS.BLACK
    : BANNER_TEXT_COLORS.WHITE;

export function getInitialMainBannerFormData(): MainBannerFormData {
  return {
    bannerName: '',
    image: null,
    text: getInitialTextFormData(),
    textColor: TextColorType.WHITE.toString(),
    link: getInitialLinkFormData(),
    isShow: false,
  };
}

export function getInitialTextFormData(): TextFormData {
  return {
    title: '',
    subtitle: '',
  };
}

export function getInitialLinkFormData(): LinkFormData {
  return {
    /**
     * [EN] Empty, not `'https://'`. Link is optional now, and a prefilled scheme reads as a value:
     *      it had to be cleared by hand to mean "no link", and a form saved without touching it
     *      sent a URL that goes nowhere.
     * [KO] `'https://'` 가 아니라 빈 값이다. Link 는 이제 선택 입력인데, 미리 채운 스킴은 값으로
     *      읽힌다 — "링크 없음" 을 뜻하려면 손으로 지워야 했고, 그대로 저장하면 아무 데도 가지
     *      않는 URL 이 넘어갔다.
     */
    url: '',
    tab: TabType.CURRENT.toString(),
  };
}

export function getInitialValidation(): MainBannerFormValidation {
  return {
    bannerName: '',
    image: '',
    title: '',
    subtitle: '',
    link: '',
  };
}

/**
 * [EN] A warning, never an error — Save stays open. The consumer crops with `object-fit: cover`,
 *      so an off-ratio image still renders; what it loses is the edges the operator chose. Telling
 *      them is worth a line, blocking them is not.
 * [KO] 오류가 아니라 경고다 — Save 는 계속 열려 있다. 소비자가 `object-fit: cover` 로 잘라 내므로
 *      비율이 어긋난 이미지도 그려진다. 잃는 것은 운영자가 고른 가장자리뿐이니, 알려 줄 가치는
 *      있어도 막을 가치는 없다.
 */
export const getImageAspectRatioWarning = ({
  width,
  height,
}: {
  width: number;
  height: number;
}): string => {
  if (!width || !height) return '';

  const ratio = width / height;
  const difference = Math.abs(ratio - IMAGE_ASPECT_RATIO) / IMAGE_ASPECT_RATIO;

  if (difference <= IMAGE_ASPECT_RATIO_TOLERANCE) return '';

  return `This image is ${width} × ${height} (${ratio.toFixed(
    3,
  )} : 1) and will be cropped. ${RECOMMENDED_IMAGE_WIDTH} × ${RECOMMENDED_IMAGE_HEIGHT} (${IMAGE_ASPECT_RATIO} : 1) is recommended.`;
};

export const isValidLinkUrl = (url: string): boolean => {
  try {
    const { protocol } = new URL(url);

    return protocol === 'http:' || protocol === 'https:';
  } catch {
    return false;
  }
};

/**
 * [EN] Length is checked here as well as capped while typing, because a banner saved under the old
 *      limits can load with more text than the new ones allow. `TextField` keeps what it is given
 *      and only caps what is typed, so the over-limit text survives on screen and this error is
 *      what stops it from being saved again.
 * [KO] 입력 중 잘라 내면서도 여기서 길이를 다시 보는 이유는, 예전 상한으로 저장된 배너가 새 상한을
 *      넘는 문구를 들고 열릴 수 있기 때문이다. `TextField` 는 받은 값을 그대로 두고 타이핑만
 *      막으므로 넘치는 문구가 화면에 남는다 — 그것이 다시 저장되지 않게 붙잡는 것이 이 오류다.
 */
export const validateFormData = (formData: MainBannerFormData): MainBannerFormValidation => {
  const { bannerName, image, text, link } = formData;

  return {
    bannerName: getRequiredTextError(bannerName, BANNER_NAME_MAX),
    /**
     * [EN] Presence only — format and size are judged where the file is chosen
     *      (`DragDropZone`), and that is the only place they can be judged. A file that got past
     *      the picker is by definition acceptable, and a file rebuilt from storage carries
     *      whatever `Content-Type` the CDN returned, so checking it again could only ever be
     *      wrong: a jpg saved yesterday came back as `application/octet-stream` and Save refused
     *      to let anyone edit the banner's text.
     * [KO] 있는지만 본다. 형식과 용량은 파일을 고르는 자리(`DragDropZone`)에서 판정하고, 판정할 수
     *      있는 자리도 거기뿐이다. 고르는 단계를 통과한 파일은 정의상 적합하고, 저장소에서 되살린
     *      파일은 CDN 이 준 `Content-Type` 을 그대로 들고 오므로 다시 검사하면 틀릴 수밖에 없다 —
     *      어제 저장한 jpg 가 `application/octet-stream` 으로 돌아와, 배너 문구를 고치려는 사람까지
     *      Save 에서 막혔다.
     */
    image: image ? '' : REQUIRED_MESSAGE,
    title: getOptionalTextError(text.title, TEXT_TITLE_MAX),
    subtitle: getOptionalTextError(text.subtitle, TEXT_SUBTITLE_MAX),
    link: getLinkError(link.url),
  };
};

const getRequiredTextError = (value: string, max: number): string => {
  if (!value) return REQUIRED_MESSAGE;

  return getOptionalTextError(value, max);
};

const getOptionalTextError = (value: string, max: number): string =>
  value.length > max ? getOverLengthMessage(max) : '';

const getLinkError = (url: string): string => {
  if (!url) return '';
  if (url.length > LINK_URL_MAX) return getOverLengthMessage(LINK_URL_MAX);
  if (!isValidLinkUrl(url)) return LINK_INVALID_MESSAGE;

  return '';
};

export const isFormValid = (validation: MainBannerFormValidation): boolean =>
  Object.values(validation).every(message => !message);

/**
 * [EN] `Image` replaces the old `BackgroundImage` + `MainObject` pair and `TextColor` replaces
 *      `Theme`, as the single-layout endpoint (BE CP-4555) takes them; the rest of the names are
 *      the ones the endpoint already took.
 * [KO] 단일 레이아웃 엔드포인트(BE CP-4555)가 받는 대로 `Image` 가 옛 `BackgroundImage` +
 *      `MainObject` 짝을, `TextColor` 가 `Theme` 를 대신한다. 나머지 이름은 엔드포인트가 이미
 *      받던 것 그대로다.
 */
export function transformToFormData(data: MainBannerFormData): FormData {
  const { bannerName, image, text, textColor, link, isShow } = data;

  const formData = new FormData();
  formData.set('IsShow', isShow.toString());
  formData.set('Name', bannerName);
  formData.set('Image', image as File);
  formData.set('Title', text.title);
  formData.set('SubTitle', text.subtitle);
  formData.set('TextColor', textColor);
  formData.set('Link', link.url);
  formData.set('LinkOpenTab', link.tab);

  return formData;
}

const IMAGE_TYPE_BY_EXTENSION: Record<string, string> = {
  jpg: 'image/jpeg',
  jpeg: 'image/jpeg',
  png: 'image/png',
};

/**
 * [EN] The MIME type of a stored image, read from its path. The file the edit form starts from is
 *      re-downloaded from storage, and `Blob.type` there is whatever the CDN's `Content-Type`
 *      header said — often `application/octet-stream`, or empty when the response is opaque. The
 *      extension is the one thing about a stored image that does not depend on a header, and the
 *      type has to be right because this File is what an update sends back.
 * [KO] 저장된 이미지의 MIME 타입을 경로에서 읽는다. 수정 폼이 시작점으로 쓰는 파일은 저장소에서 다시
 *      받아 온 것이라 `Blob.type` 이 CDN 의 `Content-Type` 헤더가 말한 값이다 — 흔히
 *      `application/octet-stream` 이고, 응답이 불투명하면 빈 값이다. 저장된 이미지에 대해 헤더에
 *      기대지 않는 정보는 확장자뿐이고, 이 File 을 그대로 수정 요청에 실어 보내므로 타입이 맞아야 한다.
 */
export const getImageTypeFromPath = (path: string): string => {
  const [pathname] = path.split(/[?#]/);
  const extension = pathname.split('.').pop()?.toLowerCase() ?? '';

  return IMAGE_TYPE_BY_EXTENSION[extension] ?? '';
};

/** 저장소가 준 타입이 우리가 아는 형식이면 그대로 쓰고, 아니면 경로의 확장자로 바로잡는다 */
const toTypedImage = (storedImage: File, imagePath: string): File =>
  IMAGE_ACCEPT_TYPES.includes(storedImage.type)
    ? storedImage
    : new File([storedImage], storedImage.name, { type: getImageTypeFromPath(imagePath) });

export async function parseMainBannerData({
  imagePath,
  name,
  title,
  subTitle,
  textColor,
  link,
  linkOpenTab,
  isShow,
}: MainBannerData): Promise<MainBannerFormData> {
  return {
    bannerName: name,
    image: toTypedImage(await convertURLtoFile(imagePath), imagePath),
    text: {
      title: title || '',
      subtitle: subTitle || '',
    },
    textColor: textColor.toString(),
    link: {
      url: link || '',
      tab: linkOpenTab.toString(),
    },
    isShow,
  };
}
